// EXTERNAL
import { unzipSync, strFromU8 } from "fflate";

/**
 * JH (30-07-26)
 * Client-side inspection of a bring-your-own HTML5 ad package (.zip), for instant pre-upload feedback
 * in the New Ad "Custom" flow. It unzips in the browser and mirrors the SERVER validator
 * (server/src/api/rest/ingestPackage.js) as closely as possible so the client and server never
 * disagree — the server re-validates on ingest and remains the source of truth.
 */

export type FileKind = "html" | "js" | "css" | "img" | "font" | "json" | "other";

export interface PackageFile {
  path: string;
  size: number; // uncompressed bytes
  kind: FileKind;
  isEntry: boolean; // the manifest source (index.html)
}

export interface PackageInspection {
  valid: boolean;
  problems: string[];
  warnings: string[];
  files: PackageFile[];
  zipBytes: number; // the .zip file size (compressed) — what the 5MB cap is measured against
  totalBytes: number; // sum of uncompressed file sizes
  width: number | null;
  height: number | null;
  clickTagCount: number;
  destinationUrl: string | null;
}

const ALLOWED_HOSTS = ["cdn.picnic-media.com", "s1.adform.net", "s0.2mdn.net"];
const MAX_ZIP_BYTES = 5 * 1024 * 1024;

const EXT_KIND: Record<string, FileKind> = {
  html: "html",
  js: "js",
  css: "css",
  json: "json",
  png: "img",
  jpg: "img",
  jpeg: "img",
  gif: "img",
  svg: "img",
  webp: "img",
  woff: "font",
  woff2: "font",
  ttf: "font",
  otf: "font",
};

function kindFor(path: string): FileKind {
  const ext = path.slice(path.lastIndexOf(".") + 1).toLowerCase();
  return EXT_KIND[ext] || "other";
}

// Brace-match the object passed to Picnic.init({...}) so a nested events:{...} doesn't confuse a naive
// regex. Ported from extractInitObject in the server validator.
function extractInitObject(html: string): { width: number | null; height: number | null; events: Record<string, number> } | null {
  const at = html.indexOf("Picnic.init(");
  if (at === -1) return null;
  const open = html.indexOf("{", at);
  if (open === -1) return null;
  let depth = 0;
  let end = -1;
  for (let i = open; i < html.length; i++) {
    if (html[i] === "{") depth++;
    else if (html[i] === "}" && --depth === 0) {
      end = i;
      break;
    }
  }
  if (end === -1) return null;
  const body = html.slice(open, end + 1);
  const w = body.match(/width:\s*(\d+)/);
  const h = body.match(/height:\s*(\d+)/);
  const events: Record<string, number> = {};
  const ev = body.match(/events:\s*\{([\s\S]*?)\}/);
  if (ev) for (const m of ev[1].matchAll(/([A-Za-z_$][\w$]*)\s*:\s*(\d+)/g)) events[m[1]] = Number(m[2]);
  return { width: w ? Number(w[1]) : null, height: h ? Number(h[1]) : null, events };
}

function empty(file: File, problems: string[]): PackageInspection {
  return { valid: false, problems, warnings: [], files: [], zipBytes: file.size, totalBytes: 0, width: null, height: null, clickTagCount: 0, destinationUrl: null };
}

export async function inspectPackage(file: File): Promise<PackageInspection> {
  const problems: string[] = [];
  const warnings: string[] = [];

  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(new Uint8Array(await file.arrayBuffer()));
  } catch {
    return empty(file, ["Could not read the uploaded zip."]);
  }

  // fflate keys directory placeholders with a trailing slash; drop them.
  const files: PackageFile[] = Object.keys(entries)
    .filter((p) => !p.endsWith("/"))
    .map((p) => ({ path: p, size: entries[p].length, kind: kindFor(p), isEntry: p === "index.html" }));
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
  const byName = entries;

  const indexData = byName["index.html"];
  if (!indexData) problems.push('missing entry file "index.html"');
  const html = indexData ? strFromU8(indexData) : "";

  let manifest: any = null;
  if (!byName["manifest.json"]) {
    problems.push("missing manifest.json");
  } else {
    try {
      manifest = JSON.parse(strFromU8(byName["manifest.json"]));
    } catch {
      problems.push("manifest.json is not valid JSON");
    }
  }

  let width: number | null = null;
  let height: number | null = null;
  let clickTagCount = 0;
  let destinationUrl: string | null = null;

  if (manifest) {
    if (!manifest.width || !manifest.height) problems.push("manifest.json missing width/height");
    if (!manifest.clicktags || !manifest.clicktags.clickTAG) problems.push("manifest.json missing clicktags.clickTAG");
    if (manifest.source !== "index.html") problems.push(`manifest.json source should be "index.html" (got "${manifest.source}")`);

    width = manifest.width ? Number(manifest.width) : null;
    height = manifest.height ? Number(manifest.height) : null;
    clickTagCount = manifest.clicktags ? Object.keys(manifest.clicktags).length : 0;
    destinationUrl = (manifest.clicktags && manifest.clicktags.clickTAG) || null;

    const meta = html.match(/name=["']ad\.size["']\s+content=["']width=(\d+)\s*,\s*height=(\d+)["']/i);
    if (!meta) problems.push('missing <meta name="ad.size" content="width=W,height=H">');
    else if (meta[1] !== String(manifest.width) || meta[2] !== String(manifest.height)) problems.push(`ad.size meta (${meta[1]}x${meta[2]}) != manifest (${manifest.width}x${manifest.height})`);

    const init = extractInitObject(html);
    if (init) {
      if (init.width != null && String(init.width) !== String(manifest.width)) problems.push(`Picnic.init width (${init.width}) != manifest width (${manifest.width})`);
      if (init.height != null && String(init.height) !== String(manifest.height)) problems.push(`Picnic.init height (${init.height}) != manifest height (${manifest.height})`);
      for (const [label, num] of Object.entries(init.events)) if (String(manifest.events?.list?.[num]) !== label) problems.push(`Picnic.init event "${label}":${num} doesn't match manifest events.list["${num}"]`);
      for (const [num, label] of Object.entries(manifest.events?.list || {})) if (String(init.events[label as string]) !== String(num)) problems.push(`manifest event "${num}":"${label}" is missing or mismatched in Picnic.init events`);
    }
  }

  if (html && !/var\s+clickTag\s*=/.test(html)) problems.push("no `var clickTag = …` declared in index.html <head>");

  const refs = html ? [...html.matchAll(/(?:src|href)=["']([^"']+)["']/g)].map((m) => m[1]) : [];
  for (const ref of refs) {
    if (/^https?:\/\//i.test(ref)) {
      let host: string;
      try {
        host = new URL(ref).host;
      } catch {
        host = ref;
      }
      if (!ALLOWED_HOSTS.includes(host)) warnings.push(`external reference to non-allowlisted host: ${host}`);
    } else if (ref.startsWith("/")) {
      problems.push(`absolute/root-relative reference "${ref}" — use a package-relative path`);
    } else if (!ref.startsWith("data:") && !ref.startsWith("#") && !ref.startsWith("javascript:")) {
      const clean = ref.split(/[?#]/)[0];
      if (!byName[clean]) problems.push(`referenced asset not found in package: ${ref}`);
    }
  }

  if (html && !/design-kit\/v\d+\/picnic\.(css|js)/.test(html)) warnings.push("not loading the Picnic design-kit — brand consistency at risk");

  if (file.size > MAX_ZIP_BYTES) problems.push(`Package is ${(file.size / 1048576).toFixed(2)} MB — over the 5 MB limit.`);

  // Entry file first, then alphabetical.
  files.sort((a, b) => (a.isEntry ? -1 : b.isEntry ? 1 : a.path.localeCompare(b.path)));

  return { valid: problems.length === 0, problems, warnings, files, zipBytes: file.size, totalBytes, width, height, clickTagCount, destinationUrl };
}
